import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  closeWork,
  getWorkFinalMap,
  saveWorkClosureNotes,
  type WorkFinalMapDto,
} from "../../../obras/obrasApi";
import type {
  WorkVariationKind,
  WorkVariationStatus,
} from "../../../workVariations/workVariationsApi";
import { getFinancialSettings } from "../../../orcamentos/orcamentosApi";
import { formatCurrency, formatDateShort, formatPercent } from "../obraFormat";

const DEFAULT_WARRANTY_MONTHS = 24;

interface WorkPostMortemTabProps {
  workId: number;
}

const cardClass = "bg-card border border-border p-6";
const captionStyle = { fontSize: "var(--text-caption)" } as const;
const valueStyle = {
  fontSize: "var(--text-base)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;
const sectionTitleStyle = {
  fontSize: "var(--text-section-title)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;

// "1 234,00 € · 23%" — omite a percentagem quando desconhecida.
function amountWithPercent(amount: number, percent?: number | null): string {
  if (percent === null || percent === undefined || !Number.isFinite(percent)) {
    return formatCurrency(amount);
  }
  return `${formatCurrency(amount)} · ${formatPercent(percent)}`;
}

function formatDays(value?: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value} ${Math.abs(value) === 1 ? "dia" : "dias"}`;
}

// Verde para margens ≥ 0, destructive para negativas — reusado na cascata do
// Mapa Final.
function marginClass(value: number): string {
  return value >= 0 ? "text-green-700" : "text-destructive";
}

// Rótulos/badges das variações — espelham a sub-tab "Variações" (read-only aqui).
function variationKindLabel(kind: WorkVariationKind): string {
  return kind === "addition" ? "Trabalho a mais" : "Trabalho a menos";
}

function variationKindBadgeClass(kind: WorkVariationKind): string {
  return kind === "addition"
    ? "bg-green-500/10 text-green-700"
    : "bg-destructive/10 text-destructive";
}

function variationStatusLabel(status: WorkVariationStatus): string {
  switch (status) {
    case "approved":
      return "Aprovada";
    case "rejected":
      return "Rejeitada";
    default:
      return "Rascunho";
  }
}

function variationStatusBadgeClass(status: WorkVariationStatus): string {
  if (status === "approved") {
    return "bg-green-500/10 text-green-700 border border-border";
  }
  if (status === "rejected") {
    return "bg-destructive/10 text-destructive border border-border";
  }
  return "bg-muted text-secondary-text border border-border";
}

// Cor + texto do desvio de prazo: positivo = atraso (destructive), ≤ 0 = dentro
// do prazo (verde), desconhecido = neutro.
function scheduleVariance(days?: number | null): { label: string; className: string } {
  if (days === null || days === undefined || !Number.isFinite(days)) {
    return { label: "—", className: "text-foreground" };
  }
  if (days > 0) {
    return {
      label: `${days} ${days === 1 ? "dia" : "dias"} de atraso`,
      className: "text-destructive",
    };
  }
  if (days < 0) {
    const early = Math.abs(days);
    return {
      label: `${early} ${early === 1 ? "dia" : "dias"} adiantado`,
      className: "text-green-700",
    };
  }
  return { label: "No prazo", className: "text-green-700" };
}

function Metric({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div>
      <p className="text-secondary-text mb-1" style={captionStyle}>
        {label}
      </p>
      <p className={valueClassName ?? "text-foreground"} style={valueStyle}>
        {value}
      </p>
    </div>
  );
}

export function WorkPostMortemTab({ workId }: WorkPostMortemTabProps) {
  const [data, setData] = useState<WorkFinalMapDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // [4.12.9] Encerramento: input de garantia (pré-preenchido pelas settings),
  // confirmação inline de 2 passos e estado de submissão.
  const [warrantyMonths, setWarrantyMonths] = useState<string>(
    String(DEFAULT_WARRANTY_MONTHS),
  );
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWorkFinalMap(workId);
      setData(result);
      setNotes(result.closureNotes ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar o balanço final.");
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Pré-preenche o período de garantia com o default das settings financeiras.
  // Se o fetch falhar, mantém-se o fallback de 24 meses.
  useEffect(() => {
    let active = true;
    getFinancialSettings()
      .then((settings) => {
        if (!active) return;
        const months =
          typeof settings.defaultWarrantyMonths === "number" &&
          settings.defaultWarrantyMonths >= 0
            ? settings.defaultWarrantyMonths
            : DEFAULT_WARRANTY_MONTHS;
        setWarrantyMonths(String(months));
      })
      .catch(() => {
        /* mantém o fallback DEFAULT_WARRANTY_MONTHS */
      });
    return () => {
      active = false;
    };
  }, []);

  const handleClose = async () => {
    setClosing(true);
    setCloseError(null);
    try {
      const trimmedNotes = notes.trim();
      const parsedMonths = Number.parseInt(warrantyMonths, 10);
      const updated = await closeWork(workId, {
        warrantyMonthsOverride:
          Number.isFinite(parsedMonths) && parsedMonths >= 0
            ? parsedMonths
            : undefined,
        closureNotes: trimmedNotes === "" ? undefined : trimmedNotes,
      });
      // closeWork devolve só o post-mortem; refaz o mapa final para atualizar a
      // cascata de variações. Se o refetch falhar, aplica a resposta do close
      // por cima do estado atual, preservando as variações já carregadas.
      try {
        const finalMap = await getWorkFinalMap(workId);
        setData(finalMap);
        setNotes(finalMap.closureNotes ?? "");
      } catch {
        setData((prev) => (prev ? { ...prev, ...updated } : prev));
        setNotes(updated.closureNotes ?? "");
      }
      setConfirmClose(false);
    } catch (e) {
      setCloseError(
        e instanceof Error ? e.message : "Não foi possível encerrar a obra.",
      );
    } finally {
      setClosing(false);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    setSaveError(null);
    setSavedMessage(null);
    try {
      const trimmed = notes.trim();
      const updated = await saveWorkClosureNotes(workId, trimmed === "" ? null : trimmed);
      // saveWorkClosureNotes devolve só o post-mortem; funde por cima do estado
      // atual para não perder a secção de variações do mapa final.
      setData((prev) => (prev ? { ...prev, ...updated } : prev));
      setNotes(updated.closureNotes ?? "");
      setSavedMessage("Notas guardadas.");
    } catch (e) {
      setSaveError(
        e instanceof Error ? e.message : "Não foi possível guardar as notas.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-secondary-text">A carregar balanço final…</div>;
  }
  if (error) {
    return <div className="text-destructive">{error}</div>;
  }
  if (!data) return null;

  const schedule = scheduleVariance(data.scheduleVarianceDays);

  return (
    <div className="space-y-6">
      {/* Banner de estado */}
      {data.isClosed ? (
        <div
          className="bg-green-500/10 text-green-700 border border-border p-4"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <p style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
            Balanço final — obra encerrada
            {data.actualEndDate ? ` em ${formatDateShort(data.actualEndDate)}` : ""}
          </p>
        </div>
      ) : (
        <div
          className="bg-muted text-foreground border border-border p-4"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <p style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
            Obra ainda em curso — valores provisórios
          </p>
          <p
            className="text-secondary-text"
            style={{ fontSize: "var(--text-caption)", marginTop: 2 }}
          >
            A data real de fim é registada no encerramento da obra.
          </p>
        </div>
      )}

      {/* [4.12.9] Encerrar obra (só em curso) */}
      {!data.isClosed && (
        <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
          <h3 className="text-foreground mb-2" style={sectionTitleStyle}>
            Encerrar obra
          </h3>
          <p className="text-secondary-text mb-5" style={captionStyle}>
            Ao encerrar, a data de fim é registada e abre-se o período de garantia.
          </p>

          <div className="mb-4" style={{ maxWidth: 220 }}>
            <label
              className="block text-foreground mb-2"
              style={{
                fontSize: "var(--text-caption)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              Período de garantia (meses)
            </label>
            <input
              type="number"
              min={0}
              step={1}
              value={warrantyMonths}
              onChange={(event) => setWarrantyMonths(event.target.value)}
              className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
              style={{ borderRadius: "var(--radius-button)" }}
            />
          </div>

          <div className="mb-4">
            <label
              className="block text-foreground mb-2"
              style={{
                fontSize: "var(--text-caption)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              Notas de encerramento
            </label>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              maxLength={4000}
              placeholder="Registe as principais causas de desvio de custo, margem ou prazo…"
              className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </div>

          {confirmClose ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-secondary-text" style={captionStyle}>
                Encerrar esta obra?
              </span>
              <button
                type="button"
                onClick={handleClose}
                disabled={closing}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {closing ? "A encerrar..." : "Confirmar"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmClose(false)}
                disabled={closing}
                className="px-4 py-2 border border-border rounded text-foreground hover:bg-accent transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setCloseError(null);
                setConfirmClose(true);
              }}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Encerrar obra
            </button>
          )}

          {closeError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-4 py-3 mt-3"
              style={{ borderRadius: "var(--radius)" }}
            >
              {closeError}
            </div>
          )}
        </div>
      )}

      {/* [4.7.1] Janela de garantia (obra encerrada) */}
      {data.isClosed && (
        <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
          <h3 className="text-foreground mb-3" style={sectionTitleStyle}>
            Garantia
          </h3>
          {data.warrantyEndDate ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-foreground" style={{ fontSize: "var(--text-base)" }}>
                Garantia até {formatDateShort(data.warrantyEndDate)}
                {typeof data.warrantyMonths === "number"
                  ? ` (${data.warrantyMonths} ${data.warrantyMonths === 1 ? "mês" : "meses"})`
                  : ""}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 text-xs ${
                  data.isUnderWarranty
                    ? "bg-green-500/10 text-green-700"
                    : "bg-muted text-secondary-text"
                }`}
                style={{ borderRadius: "var(--radius)" }}
              >
                {data.isUnderWarranty ? "Em garantia" : "Garantia expirada"}
              </span>
            </div>
          ) : (
            <p className="text-secondary-text" style={captionStyle}>
              Sem período de garantia definido para esta obra.
            </p>
          )}
        </div>
      )}

      {/* [4.8.2] Mapa Final — cascata orçamento base → revisto + margem consolidada.
          Espelha o "Mapa de variações" da sub-tab Variações. */}
      <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>
          Mapa Final — Orçamento consolidado
        </h3>
        <div className="space-y-2" style={{ fontSize: "var(--text-base)" }}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-secondary-text">Orçamento base (venda)</span>
            <span className="text-foreground whitespace-nowrap">
              {formatCurrency(data.sell)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-secondary-text">+ Trabalhos a mais</span>
            <span className="text-green-700 whitespace-nowrap">
              + {formatCurrency(Math.abs(data.approvedAdditions))}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-secondary-text">− Trabalhos a menos</span>
            <span className="text-destructive whitespace-nowrap">
              − {formatCurrency(Math.abs(data.approvedReductions))}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border pt-3 mt-1">
            <span className="text-foreground font-medium">Orçamento revisto</span>
            <span
              className="text-foreground whitespace-nowrap"
              style={sectionTitleStyle}
            >
              {formatCurrency(data.revisedSell)}
            </span>
          </div>
        </div>

        <div
          className="flex flex-wrap items-center gap-4 border-t border-border pt-3 mt-3"
          style={captionStyle}
        >
          <span className="text-secondary-text">
            Custo realizado:{" "}
            <span className="text-foreground">{formatCurrency(data.actualCost)}</span>
          </span>
          <span className="text-secondary-text">
            Margem base:{" "}
            <span className={`${marginClass(data.actualMargin)} font-medium`}>
              {formatCurrency(data.actualMargin)}
            </span>
          </span>
          <span className="text-secondary-text">
            Margem revista:{" "}
            <span className={`${marginClass(data.revisedMargin)} font-medium`}>
              {formatCurrency(data.revisedMargin)} ({formatPercent(data.revisedMarginPercent)})
            </span>
          </span>
        </div>
      </div>

      {/* [4.8.2] Variações registadas — vista consolidada read-only.
          A edição/aprovação faz-se na sub-tab "Variações". */}
      <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>
          Variações registadas
        </h3>
        {data.variations.length === 0 ? (
          <p className="text-secondary-text" style={captionStyle}>
            Sem variações registadas.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 640 }}>
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th className="px-4 py-3" style={{ width: 110 }}>
                    Nº
                  </th>
                  <th className="px-4 py-3">Descrição</th>
                  <th className="px-4 py-3" style={{ width: 160 }}>
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                    Valor
                  </th>
                  <th className="px-4 py-3" style={{ width: 120 }}>
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.variations.map((variation) => {
                  const isAddition = variation.kind === "addition";
                  // Variações rejeitadas não contam no mapa: esbatemos a linha e
                  // riscamos descrição+valor (opacity-60/line-through não existem
                  // no Tailwind pré-compilado → aplicados via style inline).
                  const isRejected = variation.status === "rejected";
                  return (
                    <tr
                      key={variation.id}
                      className="border-b border-border last:border-b-0"
                      style={isRejected ? { opacity: 0.6 } : undefined}
                    >
                      <td className="px-4 py-3 break-words">
                        {variation.number || "—"}
                      </td>
                      <td
                        className="px-4 py-3 break-words"
                        style={
                          isRejected ? { textDecoration: "line-through" } : undefined
                        }
                      >
                        {variation.description || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs ${variationKindBadgeClass(variation.kind)}`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {variationKindLabel(variation.kind)}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3 text-right whitespace-nowrap font-medium ${
                          isAddition ? "text-green-700" : "text-destructive"
                        }`}
                        style={
                          isRejected ? { textDecoration: "line-through" } : undefined
                        }
                      >
                        {isAddition ? "+ " : "− "}
                        {formatCurrency(Math.abs(variation.signedAmount))}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs ${variationStatusBadgeClass(variation.status)}`}
                          style={{ borderRadius: "var(--radius)" }}
                          title={
                            variation.status === "rejected" &&
                            variation.rejectionReason
                              ? variation.rejectionReason
                              : undefined
                          }
                        >
                          {variationStatusLabel(variation.status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-secondary-text mt-4" style={captionStyle}>
          As variações são geridas na sub-tab "Variações"; esta é a vista consolidada.
        </p>
      </div>

      {/* Orçamento vs Realizado */}
      <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>
          Orçamento vs Realizado
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Metric label="Venda orçada" value={formatCurrency(data.sell)} />
          <Metric label="Custo previsto" value={formatCurrency(data.budgetedCost)} />
          <Metric label="Custo real" value={formatCurrency(data.actualCost)} />
        </div>
        <p className="text-secondary-text mt-4" style={captionStyle}>
          O custo real não inclui ainda materiais/horas de campo registados na obra.
        </p>
      </div>

      {/* Margem prevista vs real */}
      <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>
          Margem prevista vs real
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Metric
            label="Margem prevista"
            value={amountWithPercent(data.plannedMargin, data.plannedMarginPercent)}
          />
          <Metric
            label="Margem real"
            value={amountWithPercent(data.actualMargin, data.actualMarginPercent)}
            valueClassName={data.actualMargin >= 0 ? "text-green-700" : "text-destructive"}
          />
          <Metric
            label="Variação da margem"
            value={formatCurrency(data.marginVariance)}
            valueClassName={data.marginVariance >= 0 ? "text-green-700" : "text-destructive"}
          />
        </div>
      </div>

      {/* Prazo previsto vs real */}
      <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-5" style={sectionTitleStyle}>
          Prazo previsto vs real
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Metric label="Início" value={formatDateShort(data.startDate)} />
          <Metric label="Fim previsto" value={formatDateShort(data.plannedEndDate)} />
          <Metric
            label="Fim real"
            value={data.actualEndDate ? formatDateShort(data.actualEndDate) : "— (em curso)"}
          />
          <Metric label="Duração prevista" value={formatDays(data.plannedDurationDays)} />
          <Metric label="Duração real" value={formatDays(data.actualDurationDays)} />
          <Metric
            label="Desvio de prazo"
            value={schedule.label}
            valueClassName={schedule.className}
          />
        </div>
      </div>

      {/* Causas de desvio / notas de encerramento — editor pós-encerramento.
          Em curso, as notas são captadas no painel "Encerrar obra" acima. */}
      {data.isClosed && (
        <div className={cardClass} style={{ borderRadius: "var(--radius-card)" }}>
          <h3 className="text-foreground mb-5" style={sectionTitleStyle}>
            Causas de desvio / notas de encerramento
          </h3>
          <textarea
            value={notes}
            onChange={(event) => {
              setNotes(event.target.value);
              setSavedMessage(null);
            }}
            rows={5}
            maxLength={4000}
            placeholder="Registe as principais causas de desvio de custo, margem ou prazo…"
            className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
          />
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <button
              type="button"
              onClick={handleSaveNotes}
              disabled={saving}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              {saving ? "A guardar..." : "Guardar notas"}
            </button>
            {savedMessage && (
              <span className="text-green-700" style={captionStyle}>
                {savedMessage}
              </span>
            )}
          </div>
          {saveError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-4 py-3 mt-3"
              style={{ borderRadius: "var(--radius)" }}
            >
              {saveError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
